import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOJsonLd from '@/components/feature/SEOJsonLd';
import ArticleCard from '@/pages/home/components/ArticleCard';

const categoryImages: Record<string, string> = {
  'zuixin-dizhi': 'https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20gradient%20with%20soft%20rose%20and%20golden%20tones%2C%20minimalist%20design%2C%20no%20text%2C%20editorial%20quality%2C%20clean%20aesthetic%2C%20gentle%20flowing%20shapes&width=1600&height=400&seq=cat-hero-addr&orientation=landscape',
  'xianlu-gengxin': 'https://readdy.ai/api/search-image?query=Soft%20coral%20and%20amber%20abstract%20background%20with%20flowing%20organic%20curves%2C%20minimalist%20elegant%20design%2C%20no%20text%2C%20editorial%20style%2C%20warm%20peaceful%20atmosphere&width=1600&height=400&seq=cat-hero-update&orientation=landscape',
  'shiyong-zhinan': 'https://readdy.ai/api/search-image?query=Gentle%20peach%20and%20cream%20abstract%20gradient%2C%20soft%20diffused%20shapes%2C%20minimalist%20aesthetic%2C%20no%20text%2C%20clean%20editorial%20quality%2C%20calm%20harmonious%20mood&width=1600&height=400&seq=cat-hero-guide&orientation=landscape',
  'changjian-wenti': 'https://readdy.ai/api/search-image?query=Warm%20golden%20honey%20and%20blush%20abstract%20background%2C%20subtle%20organic%20textures%2C%20minimalist%20design%2C%20no%20text%2C%20editorial%20aesthetic%2C%20soft%20lighting&width=1600&height=400&seq=cat-hero-faq&orientation=landscape',
  'anquan-fanghu': 'https://readdy.ai/api/search-image?query=Delicate%20rose%20gold%20and%20warm%20sand%20abstract%20gradient%2C%20gentle%20flowing%20shapes%2C%20minimalist%20design%2C%20no%20text%2C%20editorial%20quality%2C%20peaceful%20composition&width=1600&height=400&seq=cat-hero-safety&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'zuixin-dizhi');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);
  const heroImage = categoryImages[activeSlug] || categoryImages['zuixin-dizhi'];

  return (
    <>
      <SEOJsonLd
        pageType="CollectionPage"
        pageUrl={`/category/${activeSlug}`}
        pageTitle={`${currentCategory.name} - 爱情岛论坛网址导航`}
        pageDescription={currentCategory.description}
        dateModified="2026-08-05"
      />

      <div className="relative h-[220px] md:h-[280px] overflow-hidden">
        <img
          src={heroImage}
          alt={currentCategory.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
            <div className="max-w-2xl">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                {currentCategory.name}
              </h1>
              <p className="text-white/70 text-sm md:text-base">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <div className="flex flex-wrap gap-2 py-4 border-b border-background-200/70 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50 font-medium'
                  : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {categoryArticles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pb-16">
            {categoryArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-inbox-line text-foreground-400 text-2xl"></i>
            </div>
            <p className="text-foreground-500 text-sm">
              该分类暂无文章
            </p>
          </div>
        )}
      </div>
    </>
  );
}